import React, { ReactElement, useMemo } from 'react'

import { GenericFilterEntityDataItems, RptFilterEntityInfo } from '../../Common/Types/Types'
import LocationalFiltering from './LocationalFiltering'
import MiscellaneousFiltering from './MiscellaneousFiltering'
import ScopeFiltering from './ScopeFiltering'
import { ReportTypes } from '../../../NonReact/constants/ReportTypes'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import DeviceFiltering from './DeviceTypeFiltering' // CSS imports. Commented out because of current build process.

// CSS imports. Commented out because of current build process.
// import './filterTab.scss'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telSys: number
    handleTelSysChangeByUser: (newValue: number) => void
    partitions: string[]
    setPartitions: React.Dispatch<React.SetStateAction<string[]>>
    telephoneNos: string
    setTelephoneNos: (newValue: string) => void
    telephoneNoDescriptions: string
    setTelephoneNoDescriptions: (newValue: string) => void
    devices: string
    setDevices: (newValue: string) => void
    ACDAgents: string
    setACDAgents: (newValue: string) => void
    filterLocationToFrom: number
    setFilterLocationToFrom: (newValue: number) => void
    channelTypes: number[]
    setChannelTypes: React.Dispatch<React.SetStateAction<number[]>>
    callTypeDirection: number
    setCallTypeDirection: (newValue: number) => void
    answerType: number
    setAnswerType: (newValue: number) => void
    callerID: number
    setCallerID: (newValue: number) => void
    DDIDigits: string
    setDDIDigits: (newValue: string) => void
    accountCodes: string
    setAccountCodes: (newValue: string) => void
    callIdentifier: string
    setCallIdentifier: (newValue: string) => void
    subjectFilter: string
    setSubjectFilter: (newValue: string) => void
    treatConfCallAsExternal: boolean
    setTreatConfCallAsExternal: (checked: boolean) => void
    supportsSubjectFiltering: boolean
    isReportTemplateUser: boolean
    deviceTypes: number[]
    setDeviceTypes: (newValue: number[]) => void
    currentTelSysType?: number
    reportType?: ReportTypes
}

/**
 * Filter tab of report configuration modal window.
 *
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telSys Report property.
 * @param handleTelSysChangeByUser Enhanced handler for telephone system change.
 * @param partitions Report property.
 * @param setPartitions Setter for corresponding report property.
 * @param telephoneNos Report property.
 * @param setTelephoneNos Setter for corresponding report property.
 * @param telephoneNoDescriptions Report property.
 * @param setTelephoneNoDescriptions Setter for corresponding report property.
 * @param devices Report property.
 * @param setDevices Setter for corresponding report property.
 * @param ACDAgents Report property.
 * @param setACDAgents Setter for corresponding report property.
 * @param filterLocationToFrom Report property.
 * @param setFilterLocationToFrom Setter for corresponding report property.
 * @param channelTypes Report property.
 * @param setChannelTypes Setter for corresponding report property.
 * @param callTypeDirection Report property.
 * @param setCallTypeDirection Setter for corresponding report property.
 * @param answerType Report property.
 * @param setAnswerType Setter for corresponding report property.
 * @param callerID Report property.
 * @param setCallerID Setter for corresponding report property.
 * @param DDIDigits Report property.
 * @param setDDIDigits Setter for corresponding report property.
 * @param accountCodes Report property.
 * @param setAccountCodes Setter for corresponding report property.
 * @param callIdentifier Report property.
 * @param setCallIdentifier Setter for corresponding report property.
 * @param subjectFilter Report property.
 * @param setSubjectFilter Setter for corresponding report property.
 * @param treatConfCallAsExternal Report property.
 * @param setTreatConfCallAsExternal Setter for corresponding report property.
 * @param supportsSubjectFiltering Whether the currently selected report style supports subject filtering.
 * @param isReportTemplateUser Whether the currently logged in user is a report template user.
 * @param deviceType device type filter.
 * @param setDeviceType Setter for device type filter.
 */
const FilterTab: React.FC<Props> = ({
    filterEntityInfo,
    telSys,
    handleTelSysChangeByUser,
    partitions,
    setPartitions,
    telephoneNos,
    setTelephoneNos,
    telephoneNoDescriptions,
    setTelephoneNoDescriptions,
    devices,
    setDevices,
    ACDAgents,
    setACDAgents,
    filterLocationToFrom,
    setFilterLocationToFrom,
    channelTypes,
    setChannelTypes,
    callTypeDirection,
    setCallTypeDirection,
    answerType,
    setAnswerType,
    callerID,
    setCallerID,
    DDIDigits,
    setDDIDigits,
    accountCodes,
    setAccountCodes,
    callIdentifier,
    setCallIdentifier,
    subjectFilter,
    setSubjectFilter,
    treatConfCallAsExternal,
    setTreatConfCallAsExternal,
    supportsSubjectFiltering,
    isReportTemplateUser,
    currentTelSysType,
    reportType,
    deviceTypes,
    setDeviceTypes
}: Props): ReactElement => {
    const selectedPartitionsPartIDOnTelSys: Nullable<number[]> = useMemo(
        (): Nullable<number[]> =>
            (partitions?.length ?? 0) > 0
                ? (filterEntityInfo?.EntityInfo?.Partitions?.Data?.filter(
                      (part: GenericFilterEntityDataItems): boolean => partitions.includes(part.ID)
                  )
                      ?.map(
                          (part: GenericFilterEntityDataItems): Nullable<number> =>
                              isVarNotEmpty(part.PartIDOnTelSys) ? Number(part.PartIDOnTelSys) : undefined
                      )
                      ?.filter((part: Nullable<number>): boolean => isVarNotEmpty(part)) as Nullable<number[]>)
                : undefined,
        [filterEntityInfo, partitions]
    )

    const isExtensionDeviceReport: boolean = useMemo(
        (): boolean => reportType === ReportTypes.EXTENSION_LIST,
        [reportType]
    )

    return (
        <div className='filter-tab tab-area'>
            {!isReportTemplateUser ? (
                <ScopeFiltering
                    telSys={telSys}
                    handleTelSysChangeByUser={handleTelSysChangeByUser}
                    partitions={partitions}
                    setPartitions={setPartitions}
                    filterEntityInfo={filterEntityInfo}
                />
            ) : (
                <></>
            )}
            {!isReportTemplateUser ? (
                <>
                    {isExtensionDeviceReport ? (
                        <DeviceFiltering deviceTypes={deviceTypes} setDeviceTypes={setDeviceTypes} />
                    ) : (
                        <></>
                    )}
                    <LocationalFiltering
                        filterEntityInfo={filterEntityInfo}
                        telephoneNos={telephoneNos}
                        setTelephoneNos={setTelephoneNos}
                        telephoneNoDescriptions={telephoneNoDescriptions}
                        setTelephoneNoDescriptions={setTelephoneNoDescriptions}
                        devices={devices}
                        setDevices={setDevices}
                        ACDAgents={ACDAgents}
                        setACDAgents={setACDAgents}
                        filterLocationToFrom={filterLocationToFrom}
                        setFilterLocationToFrom={setFilterLocationToFrom}
                        telSys={telSys}
                        partitions={partitions}
                        reportType={reportType}
                        currentTelSysType={currentTelSysType}
                        selectedPartitionsPartIDOnTelSys={selectedPartitionsPartIDOnTelSys}
                        deviceTypes={deviceTypes}
                    />
                </>
            ) : (
                <></>
            )}

            <MiscellaneousFiltering
                filterEntityInfo={filterEntityInfo}
                channelTypes={channelTypes}
                setChannelTypes={setChannelTypes}
                callTypeDirection={callTypeDirection}
                setCallTypeDirection={setCallTypeDirection}
                answerType={answerType}
                setAnswerType={setAnswerType}
                callerID={callerID}
                setCallerID={setCallerID}
                DDIDigits={DDIDigits}
                setDDIDigits={setDDIDigits}
                accountCodes={accountCodes}
                setAccountCodes={setAccountCodes}
                callIdentifier={callIdentifier}
                setCallIdentifier={setCallIdentifier}
                subjectFilter={subjectFilter}
                setSubjectFilter={setSubjectFilter}
                treatConfCallAsExternal={treatConfCallAsExternal}
                setTreatConfCallAsExternal={setTreatConfCallAsExternal}
                supportsSubjectFiltering={supportsSubjectFiltering}
                isReportTemplateUser={isReportTemplateUser}
                currentTelSysType={currentTelSysType}
            />
        </div>
    )
}

export default FilterTab
